import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ToolbarTooltipButton from "../../../components/common/ToolbarTooltipButton";
import Button from "../../../components/common/Button";
import HeatmapOverlay from "./HeatmapOverlay";
import { copyImageToClipboard } from "../../../utils/clipboard";
import logoIcon from "../../../assets/tips/uploadLogo.svg";
import expand from "../../../assets/tips/expand.svg";
import delet from "../../../assets/tips/delete.svg";
import copyIcon from "../../../assets/posts/copy.svg";

export default function HeatmapViewer({
  originalImage,
  heatmapImage,
  hotzones = [],
  isHeatmapLoading = false,
  originalImageSize = { width: 1, height: 1 },
  onOriginalImageLoad,
  suggestions = [],
  onImagesReady,
  imageUrl,
  onDeleteImage,
  imageLocked,
  enabled = false,
  showToolbar = true,
}) {
  const containerRef = useRef(null);
  const { t } = useTranslation();
  const [originalLoaded, setOriginalLoaded] = useState(false);
  const [heatmapLoaded, setHeatmapLoaded] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const [imgCopied, setImgCopied] = useState(false);
  const [localImageSize, setLocalImageSize] = useState({ width: 1, height: 1 });

  const imageSize = originalImageSize.width > 1 ? originalImageSize : localImageSize;

  React.useEffect(() => {
    setOriginalLoaded(false);
    setHeatmapLoaded(false);
  }, [originalImage, heatmapImage]);

  React.useEffect(() => {
    if (originalLoaded && heatmapLoaded) {
      onImagesReady?.();
    }
  }, [originalLoaded, heatmapLoaded, onImagesReady]);

  const hasExtra = suggestions.length > 3;
  const visibleSuggestions = expanded ? suggestions : suggestions.slice(0, 3);

  return (
    <div className="w-full space-y-4">
      {/* Copy / Delete toolbar */}
      {imageLocked && showToolbar && (
        <div className="flex flex-wrap items-center gap-2 p-2 bg-black-20 rounded-tr-lg rounded-tl-lg w-full relative z-20 border border-b-0 border-gray-200 justify-end">
          <ToolbarTooltipButton
            label={t("buttons.copy")}
            onClick={async () => {
              try {
                await copyImageToClipboard(imageUrl);
                setImgCopied(true);
                setTimeout(() => setImgCopied(false), 2000);
              } catch (err) {
                console.error("Copy failed", err);
              }
            }}
          >
            <img src={copyIcon} alt="copy" className="w-4 h-4" />
          </ToolbarTooltipButton>
        </div>
      )}

      {/* ===== IMAGE COMPARISON ===== */}
      <div
        ref={containerRef}
        className="relative w-full rounded-bl-lg rounded-br-lg m-0"
        style={{ aspectRatio }}
      >
        {/* Inner layer — overflow-hidden for image/heatmap clipping only */}
        <div className="absolute inset-0 overflow-hidden rounded-bl-lg rounded-br-lg bg-gray-100 select-none">
          {/* Original image */}
          <img
            src={originalImage}
            alt="Original"
            className="absolute inset-0 w-full h-full object-contain"
            onLoad={(e) => {
              setOriginalLoaded(true);
              const img = e.target;
              const w = img.naturalWidth;
              const h = img.naturalHeight;
              setAspectRatio(w / h);
              setLocalImageSize({ width: w, height: h });
              onOriginalImageLoad?.(w, h);
            }}
          />

          {/* Heatmap overlay */}
          {heatmapImage && (
            <div
              className={`absolute inset-0 overflow-hidden transition-opacity duration-300 ${
                enabled ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            >
              <img
                src={heatmapImage}
                alt="Heatmap"
                className="w-full h-full block object-contain"
                onLoad={() => setHeatmapLoaded(true)}
              />
            </div>
          )}

          {/* Heatmap loading spinner */}
          {isHeatmapLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-20">
              <div className="w-8 h-8 border-4 border-primary-400 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* Attention dots + colour scale */}
        <HeatmapOverlay hotzones={hotzones} enabled={enabled} imageSize={imageSize} />
      </div>

      {/* ===== SUGGESTIONS ===== */}
      <div className="mb-4 border border-black-600 rounded-lg bg-gray-50 h-auto md:h-[332px] relative">
        <div className="flex justify-between items-center p-3 bg-black-20 border-b">
          <div>
            <h3 className="font-bold text-lg text-black-800">{t("insights.suggestion")}</h3>
            <p className="text-sm md:text-lg text-black-600 mb-3">{t("insights.description")}</p>
          </div>
        </div>
        <div className="p-2 space-y-2 transition-all duration-300">
          {visibleSuggestions.map((s, i) => (
            <div key={i} className="border border-gray-200 hover:border-primary-500 rounded-lg p-3 bg-gray-50">
              <div className="flex sm:justify-between sm:items-start gap-2">
                <div>
                  <p className="font-bold text-black-600 uppercase text-lg">{s.title}</p>
                  <p className="sm:text-lg text-sm text-black-400">{s.text}</p>
                </div>
                {s.recommended && (
                  <span className="text-xs h-[26px] w-auto bg-primary-500 text-black-600 px-2 py-1 rounded font-bold">
                    Recommend
                  </span>
                )}
              </div>
            </div>
          ))}
          {hasExtra && (
            <Button
              variant="ghost"
              onClick={() => setExpanded((prev) => !prev)}
              className="w-full px-4 py-2"
            >
              {expanded ? "Collapse" : "Expand"}
              <img
                src={expand}
                alt="expand"
                className={`w-[19px] h-[10px] transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
              />
            </Button>
          )}
        </div>
      </div>

      {/* Copy toast */}
      {imgCopied && (
        <div className="fixed bottom-6 right-6 bg-black-800 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {t("copyToClipboard.image")}
        </div>
      )}
    </div>
  );
}